import { useEffect, useMemo, useState } from "react";
import { Badge, Button, List, Popover, Typography } from "antd";
import {
  SafetyCertificateOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import {
  ComplianceIssueCardSummary,
  useComplianceIssueCount,
} from "../../hooks/useComplianceIssueCount";
import {
  formatPendingLapseSummary,
  usePendingLapseRuns,
} from "../../hooks/usePendingLapseRuns";
import { COMPLIANCE_CARD_META } from "../../utils/documentCompliance";
import { subscribeComplianceInvalidate } from "../../utils/complianceInvalidate";

const iconStyle = (active: boolean): React.CSSProperties => ({
  fontSize: 18,
  color: active ? "#f04f05" : "#595959",
  cursor: "pointer",
  padding: 4,
});

type AdminIssueBadgesProps = {
  maintenanceEnabled?: boolean;
  complianceEnabled?: boolean;
  maintenancePath: string;
  /** When set, show Compliance shield with issue badge + drilldown */
  compliancePath?: string;
};

/**
 * App-bar Maintenance (tool) + Compliance (shield) icons with red count bubbles (99+).
 */
const AdminIssueBadges: React.FC<AdminIssueBadgesProps> = ({
  maintenanceEnabled = false,
  complianceEnabled = false,
  maintenancePath,
  compliancePath,
}) => {
  const navigate = useNavigate();
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const { pendingRuns, pendingCount } = usePendingLapseRuns(maintenanceEnabled);
  const { issueCards, apiIssueCount, reload } = useComplianceIssueCount(
    complianceEnabled && !!compliancePath
  );

  useEffect(() => {
    return subscribeComplianceInvalidate(() => {
      reload();
    });
  }, [reload]);

  const complianceCards = useMemo(() => {
    const cards: ComplianceIssueCardSummary[] = [...issueCards];
    if (maintenanceEnabled && pendingCount > 0) {
      const meta = COMPLIANCE_CARD_META.PendingLapse;
      cards.unshift({
        key: "PendingLapse",
        label: meta.Label,
        description: meta.Description,
        count: pendingCount,
      });
    }
    return cards;
  }, [issueCards, pendingCount, maintenanceEnabled]);

  const complianceCount =
    apiIssueCount + (maintenanceEnabled ? pendingCount : 0);

  if (!maintenanceEnabled && !(complianceEnabled && compliancePath)) {
    return null;
  }

  const maintenanceContent = (
    <div style={{ width: 340, maxHeight: 400, overflow: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography.Text strong>Pending lapse runs</Typography.Text>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setMaintenanceOpen(false);
            navigate(maintenancePath);
          }}
        >
          Open Maintenance
        </Button>
      </div>
      <List
        dataSource={pendingRuns}
        locale={{ emptyText: "No pending lapse runs" }}
        renderItem={(run) => {
          const summary = formatPendingLapseSummary(run);
          return (
            <List.Item
              style={{
                cursor: "pointer",
                padding: "10px 12px",
                borderBottom: "1px solid #f0f0f0",
              }}
              onClick={() => {
                setMaintenanceOpen(false);
                navigate(`${maintenancePath}/${run.id}`);
              }}
            >
              <List.Item.Meta
                title={
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {summary.title}
                  </Typography.Text>
                }
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {summary.description}
                  </Typography.Text>
                }
              />
            </List.Item>
          );
        }}
      />
    </div>
  );

  const complianceContent = (
    <div style={{ width: 360, maxHeight: 420, overflow: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography.Text strong>Compliance issues</Typography.Text>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setComplianceOpen(false);
            navigate(compliancePath!);
          }}
        >
          Open Compliance
        </Button>
      </div>
      <List
        dataSource={complianceCards}
        locale={{ emptyText: "No compliance issues" }}
        renderItem={(card) => (
          <List.Item
            style={{
              cursor: "pointer",
              padding: "10px 12px",
              borderBottom: "1px solid #f0f0f0",
            }}
            onClick={() => {
              setComplianceOpen(false);
              navigate(`${compliancePath}?card=${encodeURIComponent(card.key)}`);
            }}
          >
            <List.Item.Meta
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {card.label}
                  </Typography.Text>
                  <Badge
                    count={card.count}
                    overflowCount={99}
                    style={{ backgroundColor: "#cf1322" }}
                  />
                </div>
              }
              description={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {card.description}
                </Typography.Text>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {maintenanceEnabled ? (
        <Popover
          content={maintenanceContent}
          title={null}
          trigger="click"
          open={maintenanceOpen}
          onOpenChange={setMaintenanceOpen}
          placement="bottomRight"
        >
          <Badge
            count={pendingCount}
            size="small"
            overflowCount={99}
            style={{ backgroundColor: "#cf1322" }}
          >
            <ToolOutlined
              style={iconStyle(pendingCount > 0)}
              title="Maintenance — pending lapse runs"
            />
          </Badge>
        </Popover>
      ) : null}

      {complianceEnabled && compliancePath ? (
        <Popover
          content={complianceContent}
          title={null}
          trigger="click"
          open={complianceOpen}
          onOpenChange={setComplianceOpen}
          placement="bottomRight"
        >
          <Badge
            count={complianceCount}
            size="small"
            overflowCount={99}
            style={{ backgroundColor: "#cf1322" }}
          >
            <SafetyCertificateOutlined
              style={iconStyle(complianceCount > 0)}
              title="Compliance — outstanding issues"
            />
          </Badge>
        </Popover>
      ) : null}
    </div>
  );
};

export default AdminIssueBadges;
